import React from 'react';
import { FormattedMessage } from 'react-intl-next';

import Heading from '@atlaskit/heading';
import { FlagsProvider } from '@atlaskit/flag';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useFocus, SkipLink } from '@confluence/accessibility';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';

import { useSessionSafeDwellTime } from './utils/useSessionSafeDwellTime.ts';
import { ADVANCED_SEARCH_RESULT_SECTION_PAGE_SEGMENT_LOAD } from './perf.config.ts';
import {
	HeaderContainer,
	AdvancedSearchPage,
	AdvancedSearchContainer,
	AdvancedSearchResults,
	SkipToSearchFieldOuterWrapper,
	SkipToSearchFieldWrapper,
} from './styled.tsx';
import { ASFeedbackCollector } from './ASFeedbackCollector.tsx';
import { SearchTextField } from './SearchTextField/SearchTextField.tsx';
import { SearchResultList } from './SearchResult/List/SearchResultList.tsx';
import { SmartAnswers } from './SmartAnswers/SmartAnswers.tsx';
import { AutocorrectionComponent } from './SearchResult/Autocorrection/Autocorrection.tsx';
import { i18n } from './AdvancedSearch-i18n.ts';
import { HorizontalFilters } from './Filters/HorizontalFilterPane/index.ts';
import { PageLoadEndEvent } from './PageLoadEndEvent.tsx';
import { HorizontalFilterWrapper } from './Filters/HorizontalFilterPane/styled.tsx';

const AdvancedSearchLayoutContent = () => {
	const { locale } = useSessionData();

	const isAutocorrectionEnabled = locale.split('-')[0] === 'en';

	const [searchTextFieldRef, goToSearchTextField] = useFocus<HTMLInputElement>();

	const isNav4Enabled = useIsNav4Enabled();

	useSessionSafeDwellTime();

	return (
		<AdvancedSearchPage data-testid="advanced-search-page">
			{isNav4Enabled && (
				<HorizontalFilterWrapper>
					<HorizontalFilters />
				</HorizontalFilterWrapper>
			)}
			{!isNav4Enabled && (
				<HeaderContainer>
					<Heading as="h1" size="large">
						<FormattedMessage {...i18n.header} />
					</Heading>
					<ASFeedbackCollector />
				</HeaderContainer>
			)}
			<AdvancedSearchContainer isNav4Enabled={isNav4Enabled}>
				<AdvancedSearchResults>
					{!isNav4Enabled && <SearchTextField searchTextRef={searchTextFieldRef} />}
					{!isNav4Enabled && <HorizontalFilters />}

					<SmartAnswers />
					{!!isAutocorrectionEnabled && <AutocorrectionComponent />}
					<PageSegmentLoadStart metric={ADVANCED_SEARCH_RESULT_SECTION_PAGE_SEGMENT_LOAD} />
					<SkipToSearchFieldOuterWrapper>
						<SkipToSearchFieldWrapper>
							<SkipLink goTo={goToSearchTextField}>
								<FormattedMessage {...i18n.skipToSearchField} />
							</SkipLink>
						</SkipToSearchFieldWrapper>
					</SkipToSearchFieldOuterWrapper>
					<SearchResultList />
					<PageLoadEndEvent />
				</AdvancedSearchResults>
				<PageSegmentLoadEnd metric={ADVANCED_SEARCH_RESULT_SECTION_PAGE_SEGMENT_LOAD} />
			</AdvancedSearchContainer>
		</AdvancedSearchPage>
	);
};

export const AdvancedSearchLayout = () => (
	<UFOSegment name="advanced-search-layout">
		<FlagsProvider>
			<AdvancedSearchLayoutContent />
		</FlagsProvider>
	</UFOSegment>
);
